// ═══════════════════════════════════════════════════════════════════════════
// publicidadLegacyShape — canónica (campanasItems + campanasMetricas) → ad legacy
// ═══════════════════════════════════════════════════════════════════════════
//
// Con el flag `feature.publicidad.rollup` ON, el dashboard deja de leer la
// colección muerta `mlAds` y arma el universo per-ad desde la canónica:
//   - `campanasItems` subtipo='ads'   → status + campaña + publicación (sin métricas)
//   - `campanasMetricas` refTipo='publicacion' → métricas por item (sin status)
//
// Este mapper hace el JOIN por item_id y devuelve EXACTAMENTE el shape que
// consume `adsEnriquecidos`/el motor de análisis (campaign_id, item_id, status,
// metrics{…}), así el resto del dashboard NO se toca.
//
// Shape canónico (nucleo aplanado, ver campanasRepo.upsertCampanaItem):
//   campanaItem = { _docId|id, campanaExternalId, publicacionExternalId,
//                   subtipo:'ads', estado, … }
//   metrica     = { refExternalId(item_id), metricas: { clicks, prints, ctr,
//                   cost, cpc, acos, units_quantity, direct_amount,
//                   indirect_amount, total_amount, … } }
//
// PURO — sin React, sin Firestore. Golden-testeable.
// ═══════════════════════════════════════════════════════════════════════════

import { ventanaNominalDelDoc } from './publicidadVentanasShape'

/**
 * Índice item_id → bloque `metricas` desde los docs de campanasMetricas
 * refTipo='publicacion'.
 *
 * @param {Array<{refExternalId, metricas}>} metricasPubs
 * @returns {Map<string, Object>}
 */
export function indexarMetricasPorItem(metricasPubs = []) {
  const m = new Map()
  for (const [itemId, doc] of indexarDocsPorItem(metricasPubs)) m.set(itemId, doc.metricas || {})
  return m
}

/**
 * Índice item_id → días que cubre el gasto de ese mismo doc (`null` si no lo
 * declara). Sale del MISMO doc que las métricas —si hay dos para un item, gana el
 * mismo en los dos índices—, así la cifra y su ventana nunca vienen de docs distintos.
 *
 * @param {Array<Object>} metricasPubs
 * @returns {Map<string, number|null>}
 */
export function indexarVentanaPorItem(metricasPubs = []) {
  const m = new Map()
  for (const [itemId, doc] of indexarDocsPorItem(metricasPubs)) m.set(itemId, ventanaNominalDelDoc(doc))
  return m
}

function indexarDocsPorItem(metricasPubs) {
  const m = new Map()
  for (const doc of metricasPubs || []) {
    if (!doc || doc.refExternalId == null) continue
    m.set(String(doc.refExternalId), doc)
  }
  return m
}

/**
 * Un doc de `campanasItems` (ads) + el índice de métricas → ad en shape legacy.
 *
 * @param {Object} campanaItem — doc canónico (subtipo='ads')
 * @param {Map<string,Object>} metricasPorItem — de indexarMetricasPorItem
 * @param {Map<string,number|null>} ventanaPorItem — de indexarVentanaPorItem
 * @returns {Object|null} — { id, item_id, campaign_id, status, metrics,
 *   ventanaMetricasDias } o null
 *   si el doc no es un ad o le falta la publicación.
 */
export function adCanonicoALegacy(campanaItem, metricasPorItem = new Map(), ventanaPorItem = new Map()) {
  if (!campanaItem) return null
  if (campanaItem.subtipo && campanaItem.subtipo !== 'ads') return null
  const itemId = campanaItem.publicacionExternalId
  if (itemId == null) return null
  const id = campanaItem._docId || campanaItem.id || `${campanaItem.campanaExternalId ?? 'na'}_${itemId}`
  return {
    id,
    item_id: String(itemId),
    campaign_id: campanaItem.campanaExternalId != null ? String(campanaItem.campanaExternalId) : null,
    status: campanaItem.estado ?? null,
    metrics: metricasPorItem.get(String(itemId)) || {},
    // Los días que suma `metrics.cost`: el divisor de todo gasto llevado a /día o /mes.
    ventanaMetricasDias: ventanaPorItem.get(String(itemId)) ?? null,
  }
}

/**
 * Universo per-ad legacy desde las dos colecciones canónicas (join por item_id).
 *
 * @param {Array<Object>} campanasItemsAds — docs campanasItems subtipo='ads'
 * @param {Array<{refExternalId, metricas}>} metricasPubs — docs campanasMetricas
 *   refTipo='publicacion'
 * @returns {Array<Object>} — ads en shape legacy
 */
export function adsCanonicosALegacy(campanasItemsAds = [], metricasPubs = []) {
  const idx = indexarMetricasPorItem(metricasPubs)
  const ventanas = indexarVentanaPorItem(metricasPubs)
  const out = []
  for (const ci of campanasItemsAds || []) {
    const ad = adCanonicoALegacy(ci, idx, ventanas)
    if (ad) out.push(ad)
  }
  return out
}

/**
 * Pérdida global per-ad (Σ gasto sin retorno), espejo EXACTO del memo `kpis`
 * (PublicidadDashboard L434-441): cost sin ventas suma entero; cost > amount con
 * amount>0 suma la diferencia. Se calcula sobre el universo per-ad legacy.
 *
 * @param {Array<{metrics}>} adsLegacy
 * @returns {number}
 */
export function calcularPerdidaPerAd(adsLegacy = []) {
  let perdida = 0
  for (const ad of adsLegacy || []) {
    const m = ad?.metrics || {}
    const c = Number(m.cost) || 0
    const a = Number(m.total_amount) || 0
    if (c > 0 && a === 0) perdida += c
    else if (c > a && a > 0) perdida += (c - a)
  }
  return perdida
}
